import { alpha, type Theme } from '@mui/material/styles';

/**
 * The look of the report preview: a quiet frame, the pages on a dark desk inside it. The
 * frame takes the height it is given, and the pages scroll within it.
 */

export const frameStyles = {
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  minHeight: 0,
  borderRadius: 1,
  border: '1px solid',
  borderColor: 'divider',
  overflow: 'hidden',
};

/** Above the pages: the caller's controls on the left, what the picture is up to on the right. */
export const barStyles = {
  px: 1.5,
  py: 1,
  gap: 1.5,
  alignItems: 'center',
  flexWrap: 'wrap',
  borderBottom: '1px solid',
  borderColor: 'divider',
};

/** The progress line's room is always kept, so a request starting or ending moves nothing. */
export const progressSlotStyles = {
  height: 2,
  flexShrink: 0,
};

/** Why there is no newer picture, over the last one that could be drawn. */
export const failureStyles = {
  px: 1.5,
  py: 1.25,
  gap: 1.5,
  alignItems: 'flex-start',
  borderBottom: '1px solid',
  borderColor: 'divider',
  backgroundColor: (theme: Theme) => alpha(theme.palette.warning.main, 0.08),
};

export const failureDetailStyles = {
  display: 'block',
  mt: 0.25,
  color: 'text.secondary',
  // A renderer's message is one long technical line.
  overflowWrap: 'anywhere',
};

export const scrollerStyles = {
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  p: 2,
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  backgroundColor: (theme: Theme) => alpha(theme.palette.common.black, 0.22),
};

/** A sheet of the report. Its height comes from the PDF's own page, so nothing jumps as pages are drawn. */
export const sheetStyles = {
  flexShrink: 0,
  width: '100%',
  backgroundColor: '#fff',
  boxShadow: '0 1px 6px rgba(0, 0, 0, .45)',
  '& canvas': { display: 'block', width: '100%', height: '100%' },
};

/** A picture that is about to be replaced steps back a little. */
export const outdatedStyles = {
  opacity: 0.6,
  transition: 'opacity 200ms ease',
};

export const emptyStyles = {
  m: 'auto',
  px: 3,
  textAlign: 'center',
  color: 'text.secondary',
};

/** The data source and language pickers beside the preview: compact, on one line while it fits. */
export const sourceRowStyles = {
  gap: 1,
  alignItems: 'center',
  flexWrap: 'wrap',
  minWidth: 0,
};

export const sourceFieldStyles = {
  minWidth: 150,
  maxWidth: 240,
  '& .MuiInputBase-input': { fontSize: 13 },
};

export const localeFieldStyles = {
  minWidth: 110,
  '& .MuiInputBase-input': { fontSize: 13 },
};

/** An inspection in the picker: its name over the project it belongs to. */
export const sourceOptionStyles = {
  flexDirection: 'column',
  alignItems: 'flex-start',
  whiteSpace: 'normal',
};
